import { HistoryOutlined } from '@ant-design/icons';
import { CaretDownOutlined } from '@vindr/icons';
import { useGetReportVersions } from '@vindr/services/actions/report.action';
import { ReportVersions } from '@vindr/services/apis/report.api';
import { REPORT_STATUS } from '@vindr/services/constants/report.constant';
import { formatDateColumn } from '@vindr/utils/dateTime';
import cx from '@vindr/utils/helpers/cx';
import { Button, Dropdown } from 'antd';
import { ItemType } from 'antd/es/menu/hooks/useItems';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

const History = ({
  appearance = 'text',
  className,
  refetch,
  reportVersions,
  selectedReportVersion,
  setSelectedReportVersion,
}: {
  appearance?: 'text' | 'icon';
  className?: string;
  refetch: ReturnType<typeof useGetReportVersions>['refetch'];
  reportVersions: ReturnType<typeof useGetReportVersions>['data']['data'];
  selectedReportVersion: string;
  setSelectedReportVersion: (report_version_id: string) => void;
}) => {
  const { t } = useTranslation('Vindoc');

  const reportStatusToPerson = useCallback(
    (
      report: ReturnType<typeof useGetReportVersions>['data']['data'][0],
      index: number,
      arr: ReportVersions[]
    ) => {
      const name =
        report?.status === REPORT_STATUS?.READING
          ? report?.created_by_name
          : report?.status === REPORT_STATUS?.SUBMITTED
          ? report?.submitted_by_name
          : report?.status === REPORT_STATUS?.APPROVED
          ? report?.approved_by_name
          : '';
      return (
        <div className="grid min-h-[24px] grid-cols-[minmax(24px,auto)_minmax(75px,auto)_1fr_auto] gap-4">
          <div>{arr?.length - index}</div>
          <div>{t(REPORT_STATUS[report?.status])}</div>
          <div
            className="max-w-52"
            title={name || t('Unknown')}
            style={{
              overflow: 'hidden',
              display: '-webkit-box',
              WebkitBoxOrient: 'vertical',
              WebkitLineClamp: 1,
            }}
          >
            {name || t('Unknown')}
          </div>
          <div>{formatDateColumn(report?.action_at)}</div>
        </div>
      );
    },
    [t]
  );

  const reportMenuItems = useMemo(() => {
    return reportVersions?.map(
      (it, index, arr) =>
        ({
          key: it?.id,
          label: reportStatusToPerson(it, index, arr),
          onClick: () => setSelectedReportVersion(`${it?.id}`),
        } as ItemType)
    );
  }, [reportVersions, reportStatusToPerson, setSelectedReportVersion]);

  if (!reportVersions?.length) return null;

  return (
    <Dropdown
      className={cx('mt-1', className)}
      overlayClassName="overflow-auto max-h-screen"
      placement="bottomLeft"
      trigger={['click']}
      menu={{
        items: reportMenuItems,
        selectable: true,
        selectedKeys: [selectedReportVersion],
      }}
      onOpenChange={(open) => open && refetch?.()}
    >
      <Button
        className="m-0 flex items-center"
        size="small"
        {...(appearance === 'icon' && { type: 'text' })}
      >
        {appearance === 'text' && (
          <>
            {t('History')}
            <CaretDownOutlined className="ml-1" />
          </>
        )}
        {appearance === 'icon' && <HistoryOutlined />}
      </Button>
    </Dropdown>
  );
};

export default History;
